/**
 * Core-100 R4 — CORE-Z: the conflicts the location ingestion recorded instead of a log line
 * (`vehicle_location_ingest_conflicts`, migration 20270206003900): a ticket named a bay or spot another vehicle
 * physically holds, so the name was NOT recorded as the vehicle's location. Before this, the table had no reader.
 * A failed read says so — it is not "no conflicts".
 */
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export interface LocationIngestConflictRow {
  id: string;
  ticket_id: string;
  location_kind: string;
  bay_id: string | null;
  parking_spot_id: string | null;
  recorded_at: string;
}

export const LOCATION_CONFLICT_WINDOW_HOURS = 24;

export function describeLocationConflict(
  r: LocationIngestConflictRow,
  names: { bays: Map<string, string>; spots: Map<string, string> },
): string {
  const place = r.location_kind === 'BAY'
    ? `bay ${names.bays.get(r.bay_id ?? '') ?? '(unknown bay)'}`
    : `spot ${names.spots.get(r.parking_spot_id ?? '') ?? '(unknown spot)'}`;
  return `A ticket named ${place}, which another vehicle physically held — not recorded as its location`;
}

export function LocationIngestConflictsPanel({
  shopId,
  bayNames,
  spotNames,
}: {
  shopId: string;
  bayNames?: Map<string, string>;
  spotNames?: Map<string, string>;
}) {
  const conflicts = useQuery({
    queryKey: ['vehicle-location-ingest-conflicts', shopId],
    queryFn: async (): Promise<LocationIngestConflictRow[]> => {
      const since = new Date(Date.now() - LOCATION_CONFLICT_WINDOW_HOURS * 3_600_000).toISOString();
      const { data, error } = await supabase
        .from('vehicle_location_ingest_conflicts' as never)
        .select('id, ticket_id, location_kind, bay_id, parking_spot_id, recorded_at')
        .eq('shop_id', shopId)
        .gte('recorded_at', since)
        .order('recorded_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as unknown as LocationIngestConflictRow[];
    },
    enabled: !!shopId,
  });
  if (conflicts.isLoading) return null;
  if (conflicts.isError) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="location-conflicts-unreadable">
        Location conflicts could not be loaded. This is not the same as there being none.
      </p>
    );
  }
  const rows = conflicts.data ?? [];
  if (rows.length === 0) return null;
  const names = { bays: bayNames ?? new Map(), spots: spotNames ?? new Map() };
  return (
    <div className="rounded-md border border-amber-500/50 p-3 text-sm" data-testid="location-conflicts">
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        Location conflicts (last {LOCATION_CONFLICT_WINDOW_HOURS} h)
      </div>
      <ul className="mt-2 space-y-1">
        {rows.map((r) => (
          <li key={r.id} className="text-xs">
            <span className="text-muted-foreground">{format(new Date(r.recorded_at), 'MMM d, h:mm a')} · </span>
            {describeLocationConflict(r, names)}
          </li>
        ))}
      </ul>
    </div>
  );
}
